import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userAPI, paymentAPI } from '../utils/api';
import { checkoutCancellationFromSearch } from '../utils/checkoutCancellation';
import SubscriptionManager from '../components/SubscriptionManager';
import './Settings.css';

// Extend window interface for gtag
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

interface UserProfile {
  _id: string;
  email: string;
  name: string;
  hasApiAccess: boolean;
  isAdmin?: boolean;
  tier?: string;
  monthlyCredits?: number;
  paidTokens?: number;
  totalTokens?: number;
  monthlyCreditsResetDate?: string;
  storageUsed: number;
  storageLimit?: number;
  customDrawingStyles?: string[];
  billingInfo?: {
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  subscriptionStatus?: string;
  subscriptionCurrentPeriodEnd?: string;
  createdAt: string;
  updatedAt: string;
}

interface TokenLedger {
  _id: string;
  amount: number;
  balance: number;
  type: string;
  description: string;
  metadata?: {
    monthlyCreditsUsed?: number;
    paidTokensUsed?: number;
    remainingMonthlyCredits?: number;
    remainingPaidTokens?: number;
    addedToPaidTokens?: boolean;
    [key: string]: any;
  };
  createdAt: string;
}

interface BillingTransaction {
  _id: string;
  amount: number;
  tokens: number;
  type: string;
  status: string;
  couponCode?: string;
  discount?: number;
  finalAmount: number;
  transactionId?: string;
  createdAt: string;
}

const PACKAGES = {
  BASIC: { amount: 1, tokens: 200, bonus: 0, label: 'Basic' },
  STANDARD: { amount: 5, tokens: 1000, bonus: 10, label: 'Standard' },
  PREMIUM: { amount: 20, tokens: 4000, bonus: 20, label: 'Premium' },
  ULTIMATE: { amount: 50, tokens: 10000, bonus: 30, label: 'Ultimate' }
};

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { user: authUser, logout, setUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'subscription' | 'billing' | 'usage'>('profile');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [ledger, setLedger] = useState<TokenLedger[]>([]);
  const [billingHistory, setBillingHistory] = useState<BillingTransaction[]>([]);
  const [tiers, setTiers] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [tokenSourceFilter, setTokenSourceFilter] = useState<'all' | 'monthly' | 'paid' | 'additions'>('all');

  // Profile form
  const [name, setName] = useState('');
  const [billingInfo, setBillingInfo] = useState({
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: ''
  });
  const [customDrawingStyles, setCustomDrawingStyles] = useState<string[]>([]);
  const [newStyleName, setNewStyleName] = useState('');

  // Purchase form
  const [selectedPackage, setSelectedPackage] = useState<'BASIC' | 'STANDARD' | 'PREMIUM' | 'ULTIMATE'>('STANDARD');
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    loadProfile();
    loadTiers();
  }, []);

  useEffect(() => {
    if (activeTab === 'usage') {
      loadUsageData();
    }
  }, [activeTab]);

  // Handle payment cancellation from Stripe (success redirects to /thank-you now)
  useEffect(() => {
    const handlePaymentReturn = async () => {
      const cancellation = checkoutCancellationFromSearch(window.location.search);
      
      // Handle cancelled payments (success is now handled by /thank-you page)
      if (cancellation) {
        // Only token-purchase cancellation has a local pending transaction.
        // New Checkout URLs carry its local ID; retain session ID support for
        // links created before this change.
        if (cancellation.kind === 'payment' && cancellation.transactionId) {
          try {
            await paymentAPI.cancelTransaction(cancellation.transactionId);
          } catch (error) {
            console.error('Failed to cancel transaction:', error);
          }
        } else if (cancellation.kind === 'payment' && cancellation.legacySessionId) {
          try {
            await paymentAPI.cancelSession(cancellation.legacySessionId);
          } catch (error) {
            console.error('Failed to cancel session:', error);
          }
        }
        const message = cancellation.kind === 'subscription'
          ? 'Subscription was cancelled. No charges were made.'
          : 'Payment was cancelled. No charges were made.';
        alert(message);
        // Refresh to show updated transaction status
        if (activeTab === 'usage') {
          loadUsageData();
        }
        // Clean up URL
        window.history.replaceState({}, '', '/settings');
      }
    };
    
    handlePaymentReturn();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await userAPI.getProfile();
      console.log('Profile data:', response.user);
      console.log('Monthly Credits:', response.user.monthlyCredits);
      console.log('Paid Tokens:', response.user.paidTokens);
      console.log('Total Tokens:', response.user.totalTokens);
      setProfile(response.user);
      setName(response.user.name);
      setBillingInfo(response.user.billingInfo || {
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: ''
      });
      setCustomDrawingStyles(response.user.customDrawingStyles || []);
      
      // Update the AuthContext user as well
      setUser(response.user);
      // Also update localStorage so it persists
      localStorage.setItem('user', JSON.stringify(response.user));
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTiers = async () => {
    try {
      const response = await userAPI.getTiers();
      setTiers(response.tiers);
    } catch (error) {
      console.error('Failed to load tiers:', error);
    }
  };

  const loadUsageData = async () => {
    try {
      const [ledgerResponse, historyResponse] = await Promise.all([
        userAPI.getLedger(),
        userAPI.getBillingHistory()
      ]);
      setLedger(ledgerResponse.ledger);
      setBillingHistory(historyResponse.history);
    } catch (error) {
      console.error('Failed to load usage data:', error);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await userAPI.updateProfile({ name, billingInfo, customDrawingStyles });
      setProfile(response.user);
      alert('Profile updated successfully!');
    } catch (error: any) {
      alert('Failed to update profile: ' + error.message);
    }
  };

  const handleAddDrawingStyle = () => {
    if (!newStyleName.trim()) return;
    if (customDrawingStyles.includes(newStyleName.trim())) {
      alert('This drawing style already exists!');
      return;
    }
    setCustomDrawingStyles([...customDrawingStyles, newStyleName.trim()]);
    setNewStyleName('');
  };

  const handleDeleteDrawingStyle = (style: string) => {
    if (window.confirm(`Delete drawing style "${style}"?`)) {
      setCustomDrawingStyles(customDrawingStyles.filter(s => s !== style));
    }
  };

  const handlePurchaseTokens = async () => {
    setPurchasing(true);
    try {
      // Create Stripe Checkout Session (promo code can be entered in Stripe Checkout)
      const { url } = await paymentAPI.createCheckoutSession(
        selectedPackage.toUpperCase()
      );
      
      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (error: any) {
      alert('Payment initialization failed: ' + error.message);
      setPurchasing(false);
    }
  };

  const calculatePrice = () => {
    const pkg = PACKAGES[selectedPackage];
    return pkg.amount;
  };

  const calculateTokens = () => {
    const pkg = PACKAGES[selectedPackage];
    return Math.floor(pkg.tokens * (1 + pkg.bonus / 100));
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  if (!profile) {
    return <div>Failed to load profile</div>;
  }

  return (
    <div className="settings-container">
      <header className="settings-header">
        <h1>⚙️ Settings</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
      </header>

      <div className="settings-tabs">
        <button
          className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Profile
        </button>
        <button
          className={`tab ${activeTab === 'subscription' ? 'active' : ''}`}
          onClick={() => setActiveTab('subscription')}
        >
          Subscription
        </button>
        <button
          className={`tab ${activeTab === 'billing' ? 'active' : ''}`}
          onClick={() => setActiveTab('billing')}
        >
          Buy Tokens
        </button>
        <button
          className={`tab ${activeTab === 'usage' ? 'active' : ''}`}
          onClick={() => setActiveTab('usage')}
        >
          Usage History
        </button>
      </div>

      <div className="settings-content">
        {loading && (
          <div className="loading-message">
            <p>Loading profile...</p>
          </div>
        )}

        {!loading && activeTab === 'subscription' && tiers && (
          <SubscriptionManager tiers={tiers} />
        )}

        {!loading && profile && activeTab === 'profile' && (
          <div className="profile-section">
            <h2>User Information</h2>
            <form onSubmit={handleUpdateProfile}>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={profile.email}
                  disabled
                />
                <p className="help-text">Email cannot be changed</p>
              </div>

              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <h3>Billing Address</h3>
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  className="form-input"
                  value={billingInfo.address}
                  onChange={(e) => setBillingInfo({ ...billingInfo, address: e.target.value })}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    className="form-input"
                    value={billingInfo.city}
                    onChange={(e) => setBillingInfo({ ...billingInfo, city: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    className="form-input"
                    value={billingInfo.state}
                    onChange={(e) => setBillingInfo({ ...billingInfo, state: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Zip Code</label>
                  <input
                    type="text"
                    className="form-input"
                    value={billingInfo.zipCode}
                    onChange={(e) => setBillingInfo({ ...billingInfo, zipCode: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <input
                    type="text"
                    className="form-input"
                    value={billingInfo.country}
                    onChange={(e) => setBillingInfo({ ...billingInfo, country: e.target.value })}
                  />
                </div>
              </div>

              <button type="submit" className="btn btn-primary">
                Save Changes
              </button>
            </form>

            <div className="account-stats">
              <h3>Account Statistics</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-label">Current Tier</div>
                  <div className="stat-value">{(profile.tier || 'free').charAt(0).toUpperCase() + (profile.tier || 'free').slice(1)}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Monthly Credits</div>
                  <div className="stat-value">{(profile.monthlyCredits || 0).toLocaleString()}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Paid Tokens</div>
                  <div className="stat-value">{(profile.paidTokens || 0).toLocaleString()}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Total Available</div>
                  <div className="stat-value">{(profile.totalTokens || 0).toLocaleString()}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Member Since</div>
                  <div className="stat-value">{new Date(profile.createdAt).toLocaleDateString()}</div>
                </div>
              </div>
              
              <div className="storage-usage">
                <div className="storage-header">
                  <h4>Storage Usage</h4>
                  <div className="storage-stats">
                    <span className="storage-used">{formatBytes(profile.storageUsed)}</span>
                    <span className="storage-separator">/</span>
                    <span className="storage-limit">{profile.isAdmin ? 'Unlimited' : formatBytes(profile.storageLimit || 500 * 1024 * 1024)}</span>
                  </div>
                </div>
                {!profile.isAdmin && (
                  <>
                    <div className="storage-progress-bar">
                      <div 
                        className="storage-progress-fill" 
                        style={{ 
                          width: `${Math.min((profile.storageUsed / (profile.storageLimit || 500 * 1024 * 1024)) * 100, 100)}%`,
                          backgroundColor: profile.storageUsed / (profile.storageLimit || 500 * 1024 * 1024) > 0.9 ? '#dc3545' : profile.storageUsed / (profile.storageLimit || 500 * 1024 * 1024) > 0.7 ? '#ffc107' : '#28a745'
                        }}
                      />
                    </div>
                    <p className="storage-help-text">
                      {profile.storageUsed / (profile.storageLimit || 500 * 1024 * 1024) > 0.9 
                        ? '⚠️ Storage almost full. Please delete some images to free up space.'
                        : profile.storageUsed / (profile.storageLimit || 500 * 1024 * 1024) > 0.7
                        ? 'ℹ️ Storage usage is high. Consider managing your images.'
                        : 'Images from your storybooks count towards storage.'}
                    </p>
                  </>
                )}
                {profile.isAdmin && (
                  <p className="storage-help-text" style={{ color: '#28a745' }}>
                    ✅ Admin account - unlimited storage
                  </p>
                )}
                <button 
                  className="btn btn-secondary" 
                  onClick={() => navigate('/files')}
                  style={{ marginTop: '15px' }}
                >
                  📁 Manage Files
                </button>
              </div>
            </div>

            <div className="custom-drawing-styles-section">
              <h3>Custom Drawing Styles</h3>
              <p className="help-text">Create your own drawing styles to use in your storybooks.</p>
              
              <div className="add-style-form">
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter new drawing style (e.g., Manga, Chibi, Graffiti)"
                  value={newStyleName}
                  onChange={(e) => setNewStyleName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddDrawingStyle()}
                />
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={handleAddDrawingStyle}
                >
                  Add Style
                </button>
              </div>

              {customDrawingStyles.length > 0 ? (
                <div className="custom-styles-list">
                  {customDrawingStyles.map((style) => (
                    <div key={style} className="custom-style-item">
                      <span>{style}</span>
                      <button
                        type="button"
                        className="btn-icon btn-delete"
                        onClick={() => handleDeleteDrawingStyle(style)}
                        title="Delete style"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-state">No custom drawing styles yet. Add one above!</p>
              )}

              <button type="button" className="btn btn-primary" onClick={handleUpdateProfile}>
                Save Drawing Styles
              </button>
            </div>
          </div>
        )}

        {!loading && profile && activeTab === 'billing' && (
          <div className="billing-section">
            <div className="token-balance">
              <h2>Token Balance</h2>
              <div className="balance-display">
                <div className="balance-item">
                  <span className="balance-label">Monthly Tokens:</span>
                  <span className="balance-amount">{(profile.monthlyCredits || 0).toLocaleString()}</span>
                </div>
                <div className="balance-item">
                  <span className="balance-label">Paid Tokens:</span>
                  <span className="balance-amount">{(profile.paidTokens || 0).toLocaleString()}</span>
                </div>
                <div className="balance-item balance-total">
                  <span className="balance-label">Total Available:</span>
                  <span className="balance-amount">{(profile.totalTokens || 0).toLocaleString()}</span>
                </div>
              </div>
              <p className="help-text">
                Monthly tokens reset on: {profile.monthlyCreditsResetDate ? new Date(profile.monthlyCreditsResetDate).toLocaleDateString() : 'N/A'}<br />
                • Character Image: 40 tokens<br />
                • Scene Composition: 20 tokens<br />
                • Text Generation: 4 tokens
              </p>
            </div>

            <h2>Purchase Tokens</h2>
            <div className="packages-grid">
              {Object.entries(PACKAGES).map(([key, pkg]) => {
                const packageKey = key as 'BASIC' | 'STANDARD' | 'PREMIUM' | 'ULTIMATE';
                const totalTokens = Math.floor(pkg.tokens * (1 + pkg.bonus / 100));
                return (
                  <div
                    key={key}
                    className={`package-card ${selectedPackage === packageKey ? 'selected' : ''}`}
                    onClick={() => setSelectedPackage(packageKey)}
                  >
                    <h3>{pkg.label}</h3>
                    <div className="package-price">${pkg.amount}</div>
                    <div className="package-tokens">
                      {totalTokens.toLocaleString()} tokens
                    </div>
                    {pkg.bonus > 0 && (
                      <div className="package-bonus">+{pkg.bonus}% BONUS</div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="purchase-summary">
              <div className="summary-row">
                <span>Package:</span>
                <span>{PACKAGES[selectedPackage].label}</span>
              </div>
              <div className="summary-row">
                <span>Tokens:</span>
                <span>{calculateTokens().toLocaleString()}</span>
              </div>
              <div className="summary-row total">
                <span>Total:</span>
                <span>${calculatePrice().toFixed(2)}</span>
              </div>
            </div>

            <button
              className="btn btn-primary btn-large"
              onClick={handlePurchaseTokens}
              disabled={purchasing}
            >
              {purchasing ? 'Processing...' : 'Purchase Tokens'}
            </button>
          </div>
        )}

        {!loading && activeTab === 'usage' && (
          <div className="usage-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0 }}>Token Usage History</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label htmlFor="token-source-filter" style={{ fontWeight: 'bold' }}>Filter by Source:</label>
                <select
                  id="token-source-filter"
                  value={tokenSourceFilter}
                  onChange={(e) => setTokenSourceFilter(e.target.value as any)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  <option value="all">All Transactions</option>
                  <option value="monthly">Monthly Tokens Only</option>
                  <option value="paid">Paid Tokens Only</option>
                  <option value="additions">Additions Only</option>
                </select>
              </div>
            </div>
            <div className="usage-table">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Source</th>
                    <th>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.filter((entry) => {
                    const monthlyUsed = entry.metadata?.monthlyCreditsUsed || 0;
                    const paidUsed = entry.metadata?.paidTokensUsed || 0;
                    const isAddition = entry.amount >= 0;

                    if (tokenSourceFilter === 'all') return true;
                    if (tokenSourceFilter === 'monthly') return monthlyUsed > 0;
                    if (tokenSourceFilter === 'paid') return paidUsed > 0;
                    if (tokenSourceFilter === 'additions') return isAddition;
                    return true;
                  }).map((entry) => {
                    const monthlyUsed = entry.metadata?.monthlyCreditsUsed || 0;
                    const paidUsed = entry.metadata?.paidTokensUsed || 0;
                    const isAddition = entry.amount >= 0;
                    const addedToPaid = entry.metadata?.addedToPaidTokens;
                    const isPurchase = entry.type === 'purchase';
                    
                    let source = '-';
                    if (monthlyUsed > 0 && paidUsed > 0) {
                      source = `${monthlyUsed} monthly + ${paidUsed} paid`;
                    } else if (monthlyUsed > 0) {
                      source = `${monthlyUsed} monthly`;
                    } else if (paidUsed > 0) {
                      source = `${paidUsed} paid`;
                    } else if (addedToPaid || isPurchase) {
                      source = 'Paid tokens';
                    } else if (isAddition) {
                      source = 'Monthly tokens';
                    }
                    
                    return (
                      <tr key={entry._id}>
                        <td>{new Date(entry.createdAt).toLocaleString()}</td>
                        <td>{entry.description}</td>
                        <td>
                          <span className={`badge badge-${entry.type}`}>
                            {entry.type}
                          </span>
                        </td>
                        <td className={entry.amount >= 0 ? 'positive' : 'negative'}>
                          {entry.amount >= 0 ? '+' : ''}{entry.amount}
                        </td>
                        <td className="token-source">{source}</td>
                        <td>{entry.balance.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {ledger.length === 0 && (
                <p className="empty-state">No usage history yet</p>
              )}
            </div>

            <h2>Billing Transactions</h2>
            <div className="billing-table">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Session</th>
                    <th>Amount</th>
                    <th>Tokens</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {billingHistory.map((transaction) => (
                    <tr key={transaction._id}>
                      <td>{new Date(transaction.createdAt).toLocaleString()}</td>
                      <td>{transaction.transactionId ? transaction.transactionId.slice(-4) : '-'}</td>
                      <td>${transaction.finalAmount.toFixed(2)}</td>
                      <td>+{transaction.tokens.toLocaleString()}</td>
                      <td>
                        <span className={`badge badge-${transaction.status}`}>
                          {transaction.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {billingHistory.length === 0 && (
                <p className="empty-state">No billing history yet</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
